/**
 * useCharlieTable is a custom hook that returns the table state and the table actions
 * similar to the tanstack table hook. The idea here is to provide similar functionality
 * while allowing the functionality to be upstreams back into the @atlaskit/table
 * if they find it acceptable.
 */
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { getRowStructs } from './table-features/get-row';
import { useRowExpansion } from './table-features/row-expanding';

export const defaultColumnWidth = {
	width: 150,
	minWidth: 40,
	maxWidth: Number.MAX_SAFE_INTEGER,
};

/**
 * Configuration and state of the CharlieTableHook object that can be used
 * to create meaningful experiences.
 *
 * What this hook doesn't do:
 * - rendering dom or React elements (this should be handled separately)
 * - styling overrides (should be handled in the rendering layer)
 */
export interface CharlieTableHook<TableDataModel extends object> {
	state?: {
		/**
		 * columnOrder is an array of keys that define the order of the columns.
		 *
		 * Default to column order of the columns property if column order not provided;
		 * This can also be used to show / hide columns if all columns are defined.
		 *
		 */
		columnOrder?: TableState['columnOrder'];
		/**
		 * sorting state is an object that contains the field and direction of the current sort.
		 * If no sort field is provided, column key is used as unique identifier.
		 *
		 * Ex. { field: 'name', direction: 'asc' }
		 */
		sorting?: TableState['sorting'];
		/**
		 * expanded state is an object that contains the keys of the rows that are expanded.
		 *
		 * Ex. { 'rowKey': true }
		 */
		expanded?: TableState['expanded'];
		/**
		 * rowSelection state is an object that contains the keys of the rows that are selected.
		 *
		 * Ex. { 'rowKey': true }
		 */
		rowSelection?: TableState['rowSelection'];
		/**
		 * columnWidths state is an object that contains the keys of the columns and their width.
		 *
		 * Ex. { 'columnKey': 100 }
		 */
		columnWidths?: TableState['columnWidths'];
		/**
		 * columnPinning state is an object that contains the keys of the columns that are pinned.
		 *
		 * Ex. { start: ['columnKey'], end: ['columnKey'] }
		 *
		 * NOTE: This is a new feature and end is not yet fully supported.
		 */
		columnPinning?: TableState['columnPinning'];
	};
	columns: CharlieTableColumn<TableDataModel>[];

	/* onColumnOrderChange is a callback to house logic to persist re-ordering or when the user wants to show/hide columns. */
	onColumnOrderChange?: OnChangeFn<TableState['columnOrder']>;
	/* onSortChange is a callback to house logic to persist sorting changes. */
	onSortChange?: OnChangeFn<TableState['sorting']>;
	/* onExpandChange is a callback to house logic to persist expanded rows. */
	onExpandChange?: OnChangeFn<TableState['expanded']>;
	/* onSelectionChange is a callback to house logic to persist selected rows. */
	onSelectionChange?: OnChangeFn<TableState['rowSelection']>;
	/**
	 * onColumnWidthsChange is a callback to house logic to persist column widths.
	 */
	onColumnWidthsChange?: OnChangeFn<TableState['columnWidths']>;
	/**
	 * onColumnPinningChange is a callback to house logic to persist column pinning.
	 */
	onColumnPinningChange?: OnChangeFn<TableState['columnPinning']>;

	/**
	 * enableRowSelection is a boolean that determines if a row can be selected.
	 * Use the function if not all rows should be selectable.
	 *
	 * NOTE: This is not fully supported when resizing or expanding.
	 */
	enableRowSelection?: boolean;

	/**
	 * Single = zero to one row at a time can be selected (no checkbox experience);
	 * Multi = zero to many rows can selected (checkbox experience);
	 * None = zero rows can be selected.
	 *
	 * TODO: make union types that if selection mode = single then selectedRowIds?: string.
	 */
	selectionMode?: 'single' | 'multi' | 'none';

	/**
	 * @private
	 * @deprecated Please start using renderChildComponent instead.
	 * getSubRows is a function that allows for sub-rows to be rendered.
	 * This is useful for rendering nested rows or rows that are not directly related to the parent row.
	 */
	getSubRows?: ({
		rowData,
		columnOrder,
	}: {
		rowData: CharlieTableRow<TableDataModel>;
		columnOrder: string[];
	}) => ReactNode;

	// Note: I want to move this to getSubRows in the future, but for now it's this until we fully deprecate the existing function
	/**
	 * getSubRows is an optional function used to access the child rows of a parent row.
	 * This is **mandatory** if you want to use the nested rows feature.
	 */
	getChildRows?: (
		row: CharlieTableRow<TableDataModel>,
	) => Array<CharlieTableRow<TableDataModel>> | undefined;

	/**
	 * @param row the row to get the id for
	 * @param index the index of the row
	 * @param parent the parent row if it exists
	 * @returns the row id
	 *
	 * This is an optional function used to get the id of a row. Defaults to the index of the row or,
	 * if nested, the indexes of the parent and child rows, separated by `.`
	 */
	getRowId?: ({
		row,
		index,
		parent,
	}: {
		row: CharlieTableRow<TableDataModel>;
		index: number;
		parent?: FinalCharlieTableRow<TableDataModel>;
	}) => string;

	/**
	 * Override the default column sizing for all columns.
	 */
	defaultColumnWidths?: {
		width: number;
		minWidth: number;
		maxWidth: number;
	};

	// Data
	rows?: CharlieTableRow<TableDataModel>[];

	initialState?: Partial<TableState>;
}

/**
 * Define a base contract for what "rows" should look like
 * while providing a way to provide stronger typing to the
 * underlying data model.
 */
export interface CharlieTableRow<TableDataModel> {
	/**
	 * @private
	 * @deprecated Use getRowId instead if needed. Otherwise, id will be autogenerated.
	 */
	key?: string;
	/* check if the specific row should be expandable */
	isExpandable?: boolean;
	/* check if the specific row should be expandable on load */
	isDefaultExpanded?: boolean;
	/* check if the row should be considered not accessible (no selection, highlighting) */
	isDisabled?: boolean;
	/* check if the row should be hidden */
	isHidden?: boolean;
	/* data for each row that will be used to render its cells */
	data: TableDataModel;
	/* overrideRender allows consumers of the table hook to override the header-based rendering pattern if the row is not using it */
	overrideRender?: (rowData: TableDataModel) => ReactNode;
	/*
	 * groupKey is used during grouping and is a string that contains the key/field that's being grouped
	 * Ex. If we're grouping by the field/key Location, "Location" would be the key.
	 */
	groupKey?: string;
	/*
	 * groupValue is used during grouping and is a string that contains the value for the key/field that's being grouped
	 * Ex. If we're grouping by Location, "USA" could be a value.
	 */
	groupValue?: string;
	subRows?: Array<CharlieTableRow<TableDataModel>>;
}

//TODO: think of a better name for this
/**
 * This is the row object after it has been processed by the table hook.
 */
export interface FinalCharlieTableRow<TableDataModel> extends CharlieTableRow<TableDataModel> {
	/**
	 * Unique Id created for the row using getRowId function.
	 */
	id: string;
	/**
	 * Depth of the row in the table.
	 */
	depth: number;
	/**
	 * Parent row if the row is a child row.
	 */
	parent?: FinalCharlieTableRow<TableDataModel>;
	/**
	 * Children rows if the row is a parent row.
	 */
	subRows?: Array<FinalCharlieTableRow<TableDataModel>>;
	/**
	 * Index of the row in the table or parent's subrows.
	 */
	index: number;
	/**
	 * Row's parent id if row is nested.
	 */
	parentId?: string;
}

export interface CharlieTableColumn<TableDataModel> {
	key: string;
	sortField?: string;
	/**
	 * If all columns are defined and no order is provided
	 * leverage isHidden to prevent columns from rendering.
	 */
	isHidden?: boolean;

	/**
	 * isSortable is a boolean that determines if a column can be sorted.
	 */
	isSortable?: boolean;

	/**
	 * sortDescription allows consumer to add a tooltip to the sort icon by specifying ascending and descending messages
	 */
	sortDescription?: {
		asc: string | ReactNode;
		desc: string | ReactNode;
	};

	/**
	 * Column description is helpful for screen readers and tooltips.
	 */
	description?: string | ReactNode;

	/**
	 * Header content of the column
	 */
	content: ReactNode;
	/**
	 * Cell controls what how data for this column is rendered in the table cell.
	 */
	cell: CharlieTableCell<TableDataModel>;

	/**
	 * Width of the column.
	 */
	width?: number;
	/**
	 * Minimum width a column can be resized to.
	 */
	minWidth?: number;

	/**
	 * Maximum width a column can be resized to.
	 */
	maxWidth?: number;
}

export interface CharlieTableCell<TableDataModel> {
	render: (rowData: TableDataModel) => ReactNode;
	colSpan?: number;
}
export const SORT_ASC = 'asc';
export const SORT_DESC = 'desc';
export type TableSortDirection = typeof SORT_ASC | typeof SORT_DESC;

export type StateUpdater<StateType> = StateType | ((prevState: StateType) => StateType);

export type OnChangeFn<StateType> = (updaterOrValue: StateUpdater<StateType>) => void;

export type TableState = {
	columnOrder: string[];
	sorting?: {
		field: string;
		direction: TableSortDirection;
	};
	expanded?: Record<string, boolean>;
	rowSelection?: Record<string, boolean>;
	columnWidths?: Record<string, number>;
	columnPinning?: {
		start?: string[];
		end?: string[];
	};
};

export type RowStruct<TableDataModel> = {
	rows: Array<FinalCharlieTableRow<TableDataModel>>;
	flatRows: Array<FinalCharlieTableRow<TableDataModel>>;
	rowsById: Record<string, FinalCharlieTableRow<TableDataModel>>;
};

export type UtilParameters<TableDataModel extends object> = {
	options: CharlieTableHook<TableDataModel>;
	getState: () => TableState;
	updateTableState: <Key extends keyof TableState>(
		key: Key,
		updater: StateUpdater<TableState[Key]>,
		externalCallback?: ((updatedValue: TableState[Key]) => void) | undefined,
	) => void;
	rowStruct?: RowStruct<TableDataModel>;
};

const useStateManagement = <TableDataModel extends object>(
	options: CharlieTableHook<TableDataModel>,
) => {
	const initialState = {
		...options.initialState,
		columnOrder: options.initialState?.columnOrder ?? options.columns?.map((column) => column.key),
	};

	const [internalState, setInternalState] = useState<TableState>(initialState);

	const updateTableState = <Key extends keyof TableState>(
		key: Key,
		updater: StateUpdater<TableState[Key]>,
		externalCallback?: (updatedValue: TableState[Key]) => void,
	) => {
		setInternalState((prevState) => {
			const currentValue = prevState[key];
			const updatedValue = typeof updater === 'function' ? updater(currentValue) : updater;

			// Optionally, call the external callback with the new value
			if (externalCallback) {
				externalCallback(updatedValue);
			}

			return { ...prevState, [key]: updatedValue };
		});
	};

	// This allows the component to either use the internal state or a provided state
	const getState = () => ({
		...internalState,
		...options.state,
	});

	return { getState, updateTableState };
};

// Column handling utilities
const useColumnUtils = <TableDataModel extends object>({
	options,
	getState,
	updateTableState,
}: UtilParameters<TableDataModel>) => {
	// Sorting utilities

	const setSorting = (newSorting: StateUpdater<TableState['sorting']>): void => {
		updateTableState('sorting', newSorting, options.onSortChange);
	};
	const getSorting = () => getState().sorting;

	const toggleSorting = (column: CharlieTableColumn<TableDataModel>): void => {
		const currentSorting = getSorting();
		if (!currentSorting || getIsCurrentSort(column) === false) {
			setSorting({
				field: column.sortField ?? column.key,
				direction: SORT_ASC,
			});
		} else {
			setSorting({
				...currentSorting,
				direction: currentSorting.direction === SORT_ASC ? SORT_DESC : SORT_ASC,
			});
		}
	};

	const getIsCurrentSort = (column: CharlieTableColumn<TableDataModel>) => {
		return getSorting()?.field === (column.sortField ?? column.key);
	};

	// Column order utilities

	const setColumnOrder = (newColumnOrder: StateUpdater<TableState['columnOrder']>): void => {
		updateTableState('columnOrder', newColumnOrder, options.onColumnOrderChange);
	};

	const getColumnsInOrder = useCallback(() => {
		return getState().columnOrder.flatMap((columnId) => {
			const column = options.columns.find((column) => column.key === columnId);
			return column ? [column] : [];
		});
	}, [getState, options.columns]);

	/**
	 * Gets the index of a column in the columnOrder array.
	 * @param columnKey -- the key of the column to find the index of
	 * @returns number -- the index of the column in the columnOrder array
	 */
	const getColumnIndex = useCallback(
		(column: CharlieTableColumn<TableDataModel>) => {
			return getColumnsInOrder().findIndex((columnInOrder) => column.key === columnInOrder.key);
		},
		[getColumnsInOrder],
	);

	const getVisibleColumns = useCallback(
		() => getColumnsInOrder().filter((column) => !column?.isHidden),
		[getColumnsInOrder],
	);

	const getColumnOrder = (): string[] => getState().columnOrder;

	// Column width utilities

	/**
	 * Sets the columnWidths state for the table.
	 * Intended for internal use or overriding the state, setColumnWidth (singular) preferred for column width changes.
	 * @param newColumnWidths -- the new columnWidths state
	 */
	const setColumnWidths = (newColumnWidths: StateUpdater<TableState['columnWidths']>): void => {
		updateTableState('columnWidths', newColumnWidths, options.onColumnWidthsChange);
	};

	/**
	 * Preferred method of changing the column width for a column.
	 * @param column -- the column to set the width for
	 * @param width -- the new width for the column
	 */
	const setColumnWidth = ({
		column,
		width,
	}: {
		column: CharlieTableColumn<TableDataModel>;
		width: number;
	}): void => {
		setColumnWidths((prevColumnWidths) => {
			return {
				...prevColumnWidths,
				[column.key]: width,
			};
		});
	};

	/**
	 * @returns the columnWidths state for the table
	 */
	const getColumnWidths = () => getState().columnWidths;

	/**
	 * Gets the width of a column, clamped between the min and max width.
	 * @param column -- the column to get the width for
	 * @returns number -- the width of the column
	 */
	const getColumnWidth = useCallback(
		(column: CharlieTableColumn<TableDataModel>) => {
			const defaultColWidth = options.defaultColumnWidths ?? defaultColumnWidth;
			return Math.max(
				column.minWidth ?? defaultColWidth.minWidth,
				Math.min(
					getState().columnWidths?.[column.key] ?? column.width ?? defaultColWidth.width,
					column.maxWidth ?? defaultColWidth.maxWidth,
				),
			);
		},
		[getState, options.defaultColumnWidths],
	);

	/**
	 * Gets the total width of all columns visible in the table.
	 * Useful for setting the width of the table container when tableLayout is fixed.
	 * @returns number -- the total width of all columns visible in the table
	 */
	const getTotalTableWidth = useCallback(() => {
		return (
			getColumnsInOrder().reduce((sum, column) => {
				if (column == null) {
					return sum;
				}
				return sum + getColumnWidth(column);
			}, 0) ?? 0
		);
	}, [getColumnWidth, getColumnsInOrder]);

	// Column pinning utilities

	/**
	 * Sets the columnPinning state for the table.
	 * @param newColumnWidths -- the new columnPinning state
	 */
	const setColumnPinning = (newColumnPinning: StateUpdater<TableState['columnPinning']>): void => {
		updateTableState('columnPinning', newColumnPinning, options.onColumnPinningChange);
	};

	/**
	 * @returns the columnPinning state for the table
	 */
	const getColumnPinning = () => getState().columnPinning;

	/**
	 * Gets the keys of all columns before the given column.
	 */
	const getStartColumnKeys = useCallback(
		(column: CharlieTableColumn<TableDataModel>): string[] =>
			getColumnsInOrder()
				.slice(0, getColumnIndex(column))
				.map((column) => column.key),
		[getColumnIndex, getColumnsInOrder],
	);

	/**
	 * Gets the keys of all columns after the given column.
	 */
	const getEndColumnKeys = useCallback(
		(column: CharlieTableColumn<TableDataModel>): string[] =>
			getColumnsInOrder()
				.slice(getColumnIndex(column) + 1)
				.map((column) => column.key),
		[getColumnIndex, getColumnsInOrder],
	);

	/**
	 * Sets the columnPinning state for the table where columns to the start or end of the given column are pinned.
	 * @param column the column to pin columns to the start or end of
	 * @param position the position to pin the columns to
	 */
	const setBulkColumnPinning = (
		column: CharlieTableColumn<TableDataModel>,
		position: 'start' | 'end',
	): void => {
		setColumnPinning((prevColumnPinning) => {
			const startColumnKeys = getStartColumnKeys(column);
			const endColumnKeys = getEndColumnKeys(column);

			const newColumnPinning = {
				start:
					position === 'start'
						? [...startColumnKeys, column.key]
						: (prevColumnPinning?.start?.filter((colkey) => colkey !== column.key) ?? []),
				end:
					position === 'end'
						? [...endColumnKeys, column.key]
						: (prevColumnPinning?.end?.filter((colkey) => colkey !== column.key) ?? []),
			};

			return newColumnPinning;
		});
	};

	/**
	 * Gets the isPinned value for a column.
	 * @param column  the column to check if it is pinned
	 * @returns the isPinned value for the column
	 */
	const getIsPinned = (column: CharlieTableColumn<TableDataModel>) => {
		const isStartPinned = getColumnPinning()?.start?.includes(column.key);

		const isEndPinned = getColumnPinning()?.end?.includes(column.key);

		return isStartPinned ? 'start' : isEndPinned ? 'end' : false;
	};

	/**
	 * Gets the total width of all columns before the given column.
	 */
	const getStartColumnWidths = useCallback(
		(column: CharlieTableColumn<TableDataModel>) =>
			getColumnsInOrder()
				.slice(0, getColumnIndex(column))
				.reduce((sum, column) => {
					if (column == null) {
						return sum;
					}
					return sum + getColumnWidth(column);
				}, 0),
		[getColumnIndex, getColumnWidth, getColumnsInOrder],
	);

	/**
	 * Gets the total width of all columns after the given column.
	 */
	const getEndColumnWidths = useCallback(
		(column: CharlieTableColumn<TableDataModel>) =>
			getColumnsInOrder()
				.slice(getColumnIndex(column) + 1)
				.reduce((sum, column) => {
					if (column == null) {
						return sum;
					}
					return sum + getColumnWidth(column);
				}, 0),
		[getColumnIndex, getColumnWidth, getColumnsInOrder],
	);

	/**
	 * Resets the columnPinning state for the table.
	 * @param column Optional column to reset pinning for. If not provided, all columns are reset.
	 */
	const resetColumnPinning = (column?: CharlieTableColumn<TableDataModel>): void => {
		setColumnPinning((prevColumnPinning) => {
			if (!column) {
				return {
					start: [],
					end: [],
				};
			}
			const newColumnPinning = {
				start: prevColumnPinning?.start?.includes(column.key) ? [] : prevColumnPinning?.start,
				end: prevColumnPinning?.end?.includes(column.key) ? [] : prevColumnPinning?.end,
			};

			return newColumnPinning;
		});
	};

	return {
		getIsCurrentSort,
		toggleSorting,
		getSorting,
		setSorting,
		setColumnOrder,
		getColumnsInOrder,
		getVisibleColumns,
		getColumnOrder,
		getColumnWidths,
		getColumnWidth,
		setColumnWidth,
		setColumnWidths,
		getTotalTableWidth,
		getStartColumnWidths,
		getEndColumnWidths,
		getColumnIndex,
		getColumnPinning,
		getIsPinned,
		setColumnPinning,
		getStartColumnKeys,
		getEndColumnKeys,
		setBulkColumnPinning,
		resetColumnPinning,
	};
};

// Row handling utilities
const useRows = <TableDataModel extends object>({
	options,
	rowStruct,
	getState,
	updateTableState,
}: UtilParameters<TableDataModel>) => {
	const setRowSelection = useCallback(
		(newRowSelection: StateUpdater<TableState['rowSelection']>) => {
			updateTableState('rowSelection', newRowSelection, options?.onSelectionChange);
		},
		[options?.onSelectionChange, updateTableState],
	);

	const getCanSelect = useCallback(
		(_row: FinalCharlieTableRow<TableDataModel>) => {
			return options.enableRowSelection;
		},
		[options],
	);

	const getIsRowSelected = useCallback(
		(row: FinalCharlieTableRow<TableDataModel>) => {
			return getState().rowSelection?.[row.id] ?? false;
		},
		[getState],
	);

	const toggleRowSelected = useCallback(
		(row: FinalCharlieTableRow<TableDataModel>): void => {
			const rowSelection = getState().rowSelection ?? {};

			const toggleSelect = (
				innerRow: FinalCharlieTableRow<TableDataModel>,
				parentSelection?: boolean,
			) => {
				if (getCanSelect(innerRow)) {
					rowSelection[innerRow.id] =
						(parentSelection ?? rowSelection?.[innerRow.id] == null)
							? true
							: !rowSelection?.[innerRow.id];
					if (innerRow.subRows) {
						innerRow.subRows.forEach((subRow) => toggleSelect(subRow, rowSelection[innerRow.id]));
					}
				}
			};

			toggleSelect(row);
			setRowSelection(() => {
				return {
					...(getState().rowSelection ?? {}),
					...rowSelection,
				};
			});
		},
		[getCanSelect, getState, setRowSelection],
	);

	//TODO: implement this
	// const getIsSomeChildrenSelected = useCallback(
	//   (row: FinalCharlieTableRow<TableDataModel>) => {
	//     const checkIfSelected = (innerRow: FinalCharlieTableRow<TableDataModel>) => {
	//       if (row.subRows) {
	//         return row.subRows.some((subRow) => getIsRowSelected(subRow));
	//       }
	//     }
	//     return false;
	//   },
	//   [getIsRowSelected],
	// );

	const getIsSomeRowsSelected = useCallback(() => {
		return Object.values(getState().rowSelection ?? {}).some(Boolean);
	}, [getState]);

	const getIsAllRowsSelected = useCallback(() => {
		const rowsExist = rowStruct?.rows?.length ?? -1 > 0;
		const anySelectionsExist = Object.keys(getState().rowSelection ?? {}).length > 0;
		const hasLength = rowsExist && anySelectionsExist;

		if (hasLength && rowStruct?.rows?.some((row) => getCanSelect(row) && !getIsRowSelected(row))) {
			return false;
		}

		return hasLength;
	}, [getCanSelect, getIsRowSelected, getState, rowStruct?.rows]);

	const toggleAllRowsSelected = useCallback((): void => {
		setRowSelection(() => {
			if (getIsAllRowsSelected()) {
				return {};
			}

			const newSelection: Record<string, boolean> = {};
			rowStruct?.flatRows?.forEach((row) => {
				newSelection[row.id] = getCanSelect(row) ? true : false;
			});

			return newSelection;
		});
	}, [getCanSelect, getIsAllRowsSelected, rowStruct?.flatRows, setRowSelection]);

	return {
		toggleRowSelected,
		getIsRowSelected,
		getIsSomeRowsSelected,
		getIsAllRowsSelected,
		toggleAllRowsSelected,
		getCanSelect,
	};
};

// Main hook
const useCharlieTable = <TableDataModel extends object>(
	options: CharlieTableHook<TableDataModel>,
) => {
	const { getState, updateTableState } = useStateManagement<TableDataModel>(options);

	const state = getState();

	//these need to have a stable reference to avoid re-renders
	const rowStruct = useMemo(() => {
		return getRowStructs({
			rows: options.rows,
			getChildRows: options.getChildRows,
			getRowId: options.getRowId,
			state: { expanded: state.expanded },
		});
	}, [options.rows, options.getChildRows, options.getRowId, state.expanded]);

	const rows = useMemo(() => {
		return rowStruct.rows;
	}, [rowStruct]);

	const columnHandlers = useColumnUtils<TableDataModel>({
		options,
		getState,
		updateTableState,
	});

	const rowHandlers = useRows<TableDataModel>({
		options,
		rowStruct,
		getState,
		updateTableState,
	});

	const rowExpansion = useRowExpansion<TableDataModel>({
		options,
		getState,
		updateTableState,
	});

	// Expose table state management functions and helper functions
	return {
		rows: rows,
		columns: options.columns,
		/**
		 * @private
		 * @deprecated getSubRows will be refactored in future versions.
		 * Use renderChildComponent instead for the existing behavior.
		 */
		getSubRows: options.getSubRows,
		rowStruct: rowStruct,
		getChildRows: options.getChildRows,
		state: getState(),
		...columnHandlers,
		...rowHandlers,
		...rowExpansion,
	};
};
type CharlieTableState = ReturnType<typeof useCharlieTable>;

export { useCharlieTable, type CharlieTableState };
